/* eslint-disable @typescript-eslint/indent */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import styled from 'styled-components';
import { generatePath } from 'react-router';
import { useIntl } from 'react-intl';
import axios, { AxiosError, AxiosResponse } from 'axios';
import {
  AlertSingleLine,
  DataTable,
  Loader,
  SdiCheck,
  SdiLicense,
  SdirButton,
  TextLink,
} from '@sdir/sds';
import { useApi } from '@sdir/httpclient/lib/hooks/useApi';
import { ErrorMessage } from '@sdir/utilities/lib/components';
import { useCurrentUser } from '@sdir/auth/lib/hooks';
import { isToggleOn } from '@sdir/blueprint.aps/lib/helpers/permissionUtilities';
import { CustomStatusLabel, ErrorWithTrace } from '@sdir/blueprint.aps/lib/components';
import { formatDateString } from '@sdir/blueprint.aps/lib/helpers/formattingHelpers';
import { PageHeader } from '../../Atoms';
import { routes } from '../../../constants/routes';
import { getApplicationApi } from '../../../httpclient';
import { GetCertificateNameByCode } from '../../../helpers/Certificates';
import {
  getSimplifiedApplicationStatus,
  mapToCustomStatusLabel,
} from '../../../helpers/applicationHelpers';
import { ToggleApplications } from '../../../constants/permissions';
import { useCertificateCodes, useCurrentPerson, useFeatureToggles } from '../../../services/hooks';
import { useNavigateWithLang } from '../../../services/hooks/useNavigateWithLang';
import { ApplicationStatus } from '../../../types';
import { ActiveApplicationsHeaders } from '../../../helpers/TableHeaders';
import { getReceiptLanguage } from '../../../helpers/languageHelpers';

const ApplicationsOverviewPage: React.FC = () => {
  const intl = useIntl();
  const navigate = useNavigateWithLang();
  const currentUser = useCurrentUser();
  const [receiptError, setReceiptError] = useState<any>();
  const [hideCompleted, setHideCompleted] = useState<boolean>(true);
  const featureToggle = useFeatureToggles();
  const codes = useCertificateCodes();

  const {
    person: { result: contactInfo },
  } = useCurrentPerson();

  const userPersonalNr = useMemo(() => {
    return contactInfo?.personalIdentificationNumber || '';
  }, [contactInfo]);

  const showApplications = !isToggleOn(featureToggle?.result, ToggleApplications, userPersonalNr);

  const {
    result: appResult,
    loading: appLoading,
    errorObject: appError,
  } = useApi({
    apiFunction: () =>
      getApplicationApi.v2GetGetownapplicationsQueryPost({
        filter: {
          status: undefined,
          freeTextSearchStringOnType: undefined,
          applicationTypes: undefined,
          excludeCertificateCodes: ['SID'],
        },
      }),
    callApiOnInit: true,
  });

  const downloadPaymentReceipt = useCallback(
    (paymentId, paidDate) => {
      const token = currentUser?.access_token || '';
      const url = `${process.env.REACT_APP_API_URL_PAYMENT_WINDOW}payment/receiptDocument`;
      setReceiptError(undefined);
      axios
        .post(
          url,
          {
            id: paymentId,
            language: { language: getReceiptLanguage(intl?.locale || '') },
          },
          {
            responseType: 'blob',
            headers: {
              'Accept-Language': intl?.locale || 'no',
              'Authorization': `Bearer ${token}`,
              'Content-Type': 'application/json',
            },
          }
        )
        .then((response: AxiosResponse) => {
          if (response.status >= 400 && response.status < 600) {
            const err = new Error(response.statusText);
            setReceiptError(err);
            throw err;
          }
          const bloburl = window.URL.createObjectURL(
            new Blob([response.data], { type: 'application/octet-stream' })
          );
          return bloburl;
        })
        .then((blobUrl: string) => {
          const link = document.createElement('a');
          link.href = blobUrl;
          const paymentDate = paidDate?.substring(0, 10);
          const filename = `Payment_Receipt_${paymentDate}.pdf`;
          link.setAttribute('download', filename);

          const clickHandler = () => {
            setTimeout(() => {
              URL.revokeObjectURL(blobUrl);
              link.removeEventListener('click', clickHandler);
              document.body.removeChild(link);
            }, 150);
          };

          document.body.appendChild(link);
          link.addEventListener('click', clickHandler, false);
          link.click();
        })
        .catch((e: AxiosError) => {
          setReceiptError(e);
        });
    },
    [currentUser, intl?.locale]
  );

  const startNewApplication = () => {
    navigate(routes.newApplication);
  };

  const { allApplications, activeApplications, hasAny, hasActive } = useMemo(() => {
    const applications = appResult?.result || [];
    const all = applications.map((a) => {
      const simpleStatus = getSimplifiedApplicationStatus(a);
      const link = generatePath(routes.application.details, { applicationUid: a.uid || '' });
      const labelStatus = mapToCustomStatusLabel(a);
      return {
        status: (
          <CustomStatusLabel
            status={labelStatus}
            text={intl.formatMessage({ id: `application.tile.status.${simpleStatus}` })}
          />
        ),
        certificate: GetCertificateNameByCode(intl?.locale, a.certificate, codes.result) || '',
        created: formatDateString(a.applicationDate || ''),
        actions: (
          <ActionsWrapper>
            {a.payment?.paymentId ? (
              <TextLink
                text={intl.formatMessage({ id: 'certificates.button.receipt' })}
                onClick={() => downloadPaymentReceipt(a.payment?.paymentId, a.payment?.paidDate)}
              />
            ) : (
              <></>
            )}
            <SdirButton
              text={intl.formatMessage({ id: 'certificates.button.showapplication' })}
              onClick={() => navigate(link)}
              size="sm"
            />
          </ActionsWrapper>
        ),
      };
    });
    const active = applications
      .filter((a) =>
        [
          ApplicationStatus.Start,
          ApplicationStatus.CheckQualification,
          ApplicationStatus.Draft,
          ApplicationStatus.ManualProcessing,
          ApplicationStatus.Payment,
          ApplicationStatus.RequestMoreInformation,
          ApplicationStatus.QASample,
        ].includes(a?.applicationStatus as ApplicationStatus)
      )
      .map((a) => {
        const simpleStatus = getSimplifiedApplicationStatus(a);
        const link = generatePath(routes.application.details, { applicationUid: a.uid || '' });
        const labelStatus = mapToCustomStatusLabel(a);
        return {
          certificate: GetCertificateNameByCode(intl?.locale, a.certificate, codes.result) || '',
          status: (
            <CustomStatusLabel
              status={labelStatus}
              text={intl.formatMessage({ id: `application.tile.status.${simpleStatus}` })}
            />
          ),
          actions: (
            <ActionsWrapper>
              {a.payment?.paymentId ? (
                <TextLink
                  text={intl.formatMessage({ id: 'certificates.button.receipt' })}
                  onClick={() => downloadPaymentReceipt(a.payment?.paymentId, a.payment?.paidDate)}
                />
              ) : (
                <></>
              )}
              <SdirButton
                text={intl.formatMessage({ id: 'certificates.button.showapplication' })}
                onClick={() => navigate(link)}
                size="sm"
              />
            </ActionsWrapper>
          ),
        };
      });
    return {
      allApplications: all,
      activeApplications: active,
      hasAny: all && all.length > 0,
      hasActive: active && active.length > 0,
    };
  }, [appResult?.result, codes?.result, intl?.locale]);

  const toggleCompleted = () => {
    setHideCompleted(!hideCompleted);
  };

  useEffect(() => {
    if (!hasActive && hasAny) {
      setHideCompleted(false);
    }
  }, [hasAny, hasActive]);

  if (!showApplications) {
    return (
      <AlertSingleLine
        title="Error"
        type="error"
        text={intl.formatMessage({ id: 'utils.errors.insufficientaccess' })}
      />
    );
  }

  return (
    <Wrapper>
      <PageHeader
        backText={intl.formatMessage({ id: 'general.form.goback' })}
        heading={intl.formatMessage({ id: 'applicationsOverview.page.title' })}
        primaryIcon={SdiLicense}
        secondaryIcon={SdiCheck}
        backClick={() => navigate(-1)}
      />
      <ButtonRow>
        <SdirButton
          type="primary"
          text={intl.formatMessage({ id: 'certificates.button.newapplication' })}
          onClick={startNewApplication}
        />
      </ButtonRow>
      {receiptError && (
        <ErrorWrapper>
          <ErrorMessage error={receiptError} />
        </ErrorWrapper>
      )}
      {appError && (
        <ErrorWrapper>
          <ErrorWithTrace error={appError} trace={appError?.response?.headers?.traceresponse} />
        </ErrorWrapper>
      )}
      {appLoading && <Loader />}
      {hasAny && (
        <ActiveApplicationsTableWrapper>
          <StyledDataTable
            data={hideCompleted ? activeApplications || [] : allApplications || []}
            columns={ActiveApplicationsHeaders(intl.formatMessage)}
          />
          <TextLink
            text={
              hideCompleted
                ? intl.formatMessage({ id: 'applications.table.showcompleted' })
                : intl.formatMessage({ id: 'applications.table.hidecompleted' })
            }
            onClick={toggleCompleted}
          />
        </ActiveApplicationsTableWrapper>
      )}
    </Wrapper>
  );
};
export default ApplicationsOverviewPage;

const Wrapper = styled.div`
  padding-top: 20px;
  padding-bottom: 60px;
  display: grid;
  .sectionheader {
    margin-top: 5em;
  }
  h1 {
    margin-bottom: 0;
  }
`;

const ActionsWrapper = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: flex-end;
  button {
    font-size: 14px;
    line-height: 14px;

    &:last-child {
      margin-left: 10px;
      min-height: 24px;
      height: auto;
      min-width: 112px;
    }
  }
`;

const ActiveApplicationsTableWrapper = styled.div`
  margin-top: 40px;
`;

const ButtonRow = styled.div`
  display: flex;
  & > button:not(:last-child) {
    margin-right: 16px;
  }

  &.centered {
    justify-content: center;
  }
`;

const ErrorWrapper = styled.div`
  margin-top: 20px;
`;

const StyledDataTable = styled(DataTable)`
  margin-left: -5px;
  background-color: transparent;
  thead tr th {
    button {
      text-align: left;
    }
  }
  tbody > tr {
    border-top: 1px solid ${({ theme }) => theme.colors.primary8};
    min-height: 40px;

    & > td {
      padding-left: 14px;
      &:last-child {
        padding-right: 14px !important;
      }
    }

    &:last-child {
      border-bottom: 1px solid ${({ theme }) => theme.colors.primary8};
    }
  }
`;
